<template>
  <div
    v-show="visible && isFrontPage"
    ref="wrap"
    class="live2d-puppet"
    :style="puppetStyle"
    @mousedown="onDragStart"
    @touchstart="onTouchStart"
    @wheel="onWheel"
    @click="react">
    <canvas ref="canvas" class="live2d-canvas"></canvas>
  </div>
  <!-- 皮套整个跑到屏幕外了就冒出来，点一下叫回来（不然主人真找不着了） -->
  <button v-if="ready && lost && isFrontPage" class="live2d-find" @click.stop="bringBack">🐋 皮套回来</button>
</template>

<script setup>
/* 前台各页左下角的 Live2D 皮套
 * - 模型/开关来自站点 setting（后台「页面管理 → Live2D 皮套」），可多选随机
 * - 位置和大小可以拖/滚轮调，参数存在浏览器本地（localStorage）
 * - 挂载在 App.vue 上（首页/作品/积分兑换/舰长礼物/歌单/关于都有），后台页面自动隐藏
 * - 切到后台再切回来不会重来一遍：设置没变就什么都不做；
 *   WebGL 上下文丢了（有些浏览器切标签会丢）会自动重载
 */
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import { fetchSiteSetting, onSettingsChanged, parsePicks, pickRandom } from '../../util/settingsBus';

const DEFAULT_SIZE = 1500;      // 容器默认 1500×1500（原来 1000）
const MIN_SIZE = 120;
const MAX_SIZE = 8192;          // 不设实际上限（这个数只是防呆，人的滚轮滚不到）
const PAINT_RATIO = 0.5;        // 皮套画面在容器里占的比例：只占中间一半（原来 0.92）
const LS_KEY = 'dsh-live2d-layout';

const route = useRoute();
const visible = ref(false);
const ready = ref(false);      // 校正完之前整块透明（先摆好再露出来，看不到中间过程）
const canvas = ref(null);
const wrap = ref(null);

// 只有前台页面显示（后台 /admin、/mainbox 隐藏，但组件不卸载，模型不用重新加载）
const isFrontPage = computed(() => {
  const p = route.path || '';
  return p.indexOf('/admin') !== 0 && p.indexOf('/mainbox') !== 0;
});

// 位置(相对左下角的 left / bottom 偏移) + 大小，全部存本地
const size = ref(DEFAULT_SIZE);
const posX = ref(0);
const posY = ref(0);

/** 皮套画面在容器里的占比（校正完记下来；只用来判断「还在不在屏幕里」） */
const paint = { l: 0, t: 0, r: 1, b: 1 };

/** 皮套（真实画面那块，不是透明容器）在视口里还剩多少 */
const visibleArea = () => {
  const vw = window.innerWidth, vh = window.innerHeight, b = size.value;
  const top0 = vh - posY.value - b;
  const l = posX.value + paint.l * b, r = posX.value + paint.r * b;
  const t = top0 + paint.t * b, bo = top0 + paint.b * b;
  const ix = Math.max(0, Math.min(r, vw) - Math.max(l, 0));
  const iy = Math.max(0, Math.min(bo, vh) - Math.max(t, 0));
  return ix * iy;
};

// 画面整个跑到屏幕外 → 给个按钮叫回来
const lost = ref(false);
const recomputeLost = () => {
  const b = size.value;
  lost.value = ready.value && visibleArea() < Math.max(400, b * b * 0.004);
};

const bringBack = () => {
  const b = size.value, vw = window.innerWidth, vh = window.innerHeight;
  posX.value = clampX(b > vw ? Math.round((vw - b) / 2) : 0);
  posY.value = clampY(0);
  recomputeLost();
  saveLayout(0);
};

const puppetStyle = computed(() => ({
  left: posX.value + 'px',
  bottom: posY.value + 'px',
  width: size.value + 'px',
  height: size.value + 'px',
  // 还没校正完 → 透明 + 不接鼠标；校正完才淡入，主人看不到「先被裁一下再跳回来」
  opacity: ready.value ? 1 : 0,
  pointerEvents: ready.value ? 'auto' : 'none'
}));

let app = null;
let model = null;
let loadedPath = '';
let loading = false;
let destroyed = false;
let offSettings = null;
let onMove = null;
let onTouch = null;
let saveTimer = 0;
let retryTimer = 0;
let lastSig = '';
let lastSetting = null;
let contextLost = false;
let chain = Promise.resolve();     // 串行化：两次「应用设置」不会互相打断

// 拖动状态
let dragging = false;
let dragMoved = false;
let dragStart = { mx: 0, my: 0, x: 0, y: 0 };

/** 位置规则（主人拍板）：随便拖、界外照存，中间那个「露一半」的状态就是要的
 *  —— 每个方向都能拖出屏幕，拖到哪存到哪，代价是拖出去了就可能被挡住；
 *  位置范围放宽到「-容器边长 ~ 视口」，想完全拖出去也行，只是得留得住抓手。 */
const rangeOf = (viewport, box) => ({ min: -box, max: viewport });

/** 边长上限 = min(防呆上限, 视口宽, 视口高)
 *  主人要求「皮套高了就缩小，让它显示完整」—— 容器不能超过屏幕，
 *  这样「皮套随容器缩放」的前提下，皮套永远能完整落在屏幕里。 */
// 容器上限：原来会被视口卡住（1080 高的屏幕最大只能 1080），1500 的容器就设不上去；
// 现在放宽到「至少 1500」，窗口再小也不会把容器压回去（皮套本身只占中间 50%，照样在屏幕里）
const maxSize = () => Math.round(Math.max(MIN_SIZE, Math.min(MAX_SIZE, Math.max(DEFAULT_SIZE, window.innerWidth, window.innerHeight))));
const clampSize = (v) => Math.max(MIN_SIZE, Math.min(maxSize(), Math.round(v)));

/** 渲染分辨率：放大时**不降**（降了就是主人说的「放大后变模糊」）
 *  小尺寸时用高清屏的 dpr（更锐利），尺寸很大时固定 1 —— 画布 = 显示尺寸，1:1 永远不糊。 */
const renderResolution = (box) => (box > 1500 ? 1 : Math.min(window.devicePixelRatio || 1, 2));

const clampX = (v) => {
  const r = rangeOf(window.innerWidth, size.value);
  return Math.max(r.min, Math.min(r.max, Math.round(v)));
};
const clampY = (v) => {
  const r = rangeOf(window.innerHeight, size.value);
  return Math.max(r.min, Math.min(r.max, Math.round(v)));
};

watch([posX, posY, size, ready], recomputeLost, { immediate: true });

const LAYOUT_VERSION = 3;   // v3：容器默认 1500×1500、皮套只占中间 50%（老缓存的 size 要重置）
const FIT_KEY = 'dsh-live2d-fit-v3';   // 每个模型「摆好的几何」存一份：下次进来直接套用，不用再量
                                        // （v3 是因为画面占比从 92% 改成 50%，按老比例摆的几何不能再用）

/** 存的是**归一化**几何（都除以容器边长 S）：
 *  kk = 模型缩放 / S、gx/gy = 模型位置 / S。
 *  因为「皮套随容器等比缩放」这条规则下这三个量跟 S 无关，
 *  所以缓存对任何尺寸都成立，滚轮放大缩小之后也照样有效。 */
const readFitCache = (path) => {
  try {
    const all = JSON.parse(localStorage.getItem(FIT_KEY) || '{}');
    const v = path && all[path];
    if (v && Number(v.kk) > 0 && isFinite(Number(v.gx)) && isFinite(Number(v.gy))) return v;
  } catch (e) {}
  return null;
};

const writeFitCache = (path) => {
  if (!path || !geometry.S) return;
  try {
    const all = JSON.parse(localStorage.getItem(FIT_KEY) || '{}');
    all[path] = { kk: geometry.k / geometry.S, gx: geometry.x / geometry.S, gy: geometry.y / geometry.S, at: Date.now() };
    const keys = Object.keys(all);
    if (keys.length > 8) {
      keys.sort((a, b) => (all[a].at || 0) - (all[b].at || 0));
      for (let i = 0; i < keys.length - 8; i++) delete all[keys[i]];
    }
    localStorage.setItem(FIT_KEY, JSON.stringify(all));
  } catch (e) {}
};

const applyFit = (fit) => {
  const S = size.value;
  model.scale.set(fit.kk * S);
  model.x = fit.gx * S;
  model.y = fit.gy * S;
  geometry.S = S;
  geometry.k = model.scale.x;
  geometry.x = model.x;
  geometry.y = model.y;
};

const loadLayout = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) {
      const v = JSON.parse(raw);
      if (v && typeof v === 'object') {
        const isNew = Number(v.v) >= 3;
        // 老缓存（没有 v 字段）是 300 时代存的，这里按新默认 1000 迁移一次
        size.value = clampSize(isNew ? (Number(v.size) || DEFAULT_SIZE) : DEFAULT_SIZE);
        if (v.pn && typeof v.pn === 'object') {          // 画面占比先读回来，判断跑没跑丢要用
          paint.l = Number(v.pn.l) || 0;
          paint.t = Number(v.pn.t) || 0;
          paint.r = Number(v.pn.r) || 1;
          paint.b = Number(v.pn.b) || 1;
        }
        posX.value = clampX(Number(v.x) || 0);
        posY.value = clampY(Number(v.y) || 0);
        if (!isNew) saveLayout(0);
        return true;
      }
    }
  } catch (e) {}
  size.value = clampSize(DEFAULT_SIZE);
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  // 容器比视口大就居中显示（不然一上来就被裁一截），否则还是老实待左下角
  posX.value = size.value > vw ? Math.round((vw - size.value) / 2) : 0;
  posY.value = size.value > vh ? Math.round((vh - size.value) / 2) : 0;
  return false;
};

const saveLayout = (delay) => {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = window.setTimeout(() => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify({
        v: LAYOUT_VERSION, size: size.value, x: posX.value, y: posY.value,
        pn: { l: paint.l, t: paint.t, r: paint.r, b: paint.b }
      }));
    } catch (e) {}
  }, delay === undefined ? 250 : delay);
};

const loadScript = (src) => new Promise((resolve, reject) => {
  const exist = document.querySelector('script[data-live2d="' + src + '"]');
  if (exist) {
    if (exist.dataset.loaded === '1') return resolve();
    exist.addEventListener('load', () => resolve());
    exist.addEventListener('error', () => reject(new Error('脚本加载失败 ' + src)));
    return;
  }
  const s = document.createElement('script');
  s.src = src;
  s.async = true;
  s.dataset.live2d = src;
  s.addEventListener('load', () => { s.dataset.loaded = '1'; resolve(); });
  s.addEventListener('error', () => reject(new Error('脚本加载失败 ' + src)));
  document.head.appendChild(s);
});

/* ---------------- 量「皮套真正画出来的像素范围」 ---------------- */

const MEASURE_SIZE = 160;      // 离屏量尺寸用的小画布（只算比例，越小越快）
const lastM = { off: null, exact: null, offMs: 0 };   // 调试用：最近一次量到的画面范围
let PIXI_MOD = null;           // loadModel 里存一份：离屏渲染要 RenderTexture
let rtMeasure = null;

/** 扫一遍像素，返回不透明像素的外接矩形（左下角原点，原始像素坐标） */
const paintBoxOf = (px, w, h) => {
  let minX = w, maxX = -1, minY = h, maxY = -1;
  for (let y = 0; y < h; y++) {
    const row = y * w;
    for (let x = 0; x < w; x++) {
      if (px[(row + x) * 4 + 3] > 8) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  return maxX < 0 ? null : { minX: minX, maxX: maxX, minY: minY, maxY: maxY };
};

const ensureMeasureRT = (R) => {
  if (!PIXI_MOD || !PIXI_MOD.RenderTexture) return null;
  if (!rtMeasure || rtMeasure.width !== R) {
    try { if (rtMeasure) rtMeasure.destroy(true); } catch (e) {}
    rtMeasure = PIXI_MOD.RenderTexture.create({ width: R, height: R });
  }
  return rtMeasure;
};

/** 把小画布的像素读回来，**统一成自上而下的行序**（左上角原点）
 *  ★ 这一步很关键：extract.pixels 走的是 canvas.getImageData，本来就是自上而下的；
 *    自己绑帧缓冲 readPixels 拿到的却是自下而上的。两种顺序混用会让「上边距」永远算成 0，
 *    于是每轮都误判成「被画布裁了」，一路缩小 —— 之前校正要滚 40 秒就是这么来的。 */
const readBack = (rt, R) => {
  const gl = app.renderer.gl;
  try {
    if (app.renderer.extract && app.renderer.extract.pixels) {
      const px = app.renderer.extract.pixels(rt);
      if (px && px.length) return px;
    }
  } catch (e) {}
  try {
    const fb = rt.framebuffer;
    if (!fb) return null;
    const fs = app.renderer.framebuffer;
    if (fs && fs.bind) fs.bind(fb); else gl.bindFramebuffer(gl.FRAMEBUFFER, fb.framebuffer);
    const raw = new Uint8Array(R * R * 4);
    gl.readPixels(0, 0, R, R, gl.RGBA, gl.UNSIGNED_BYTE, raw);
    if (fs && fs.bind) fs.bind(null); else gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    const out = new Uint8Array(R * R * 4);
    for (let y = 0; y < R; y++) out.set(raw.subarray((R - 1 - y) * R * 4, (R - y) * R * 4), y * R * 4);
    return out;
  } catch (e) {
    return null;
  }
};

/** 量一次「模型真正画出来的像素范围」（容器坐标，左上角原点）
 *  ★ exact=false（默认，校正时用）：渲染到**离屏小画布**上量 ——
 *    主画布上的画面全程一动不动，主人屏幕上永远看不到中间过程（先摆好，再渲染出来）；
 *    而且 160×160 比 805×805 少 25 倍像素，迭代飞快。
 *  ★ exact=true（收尾时用）：在主画布上按真实像素精确量一次，做最后的微调 */
const measurePainted = (exact) => {
  if (!app || !model) return null;
  try {
    const gl = app.renderer.gl;
    if (exact) {
      app.renderer.render(app.stage);
      const w = gl.drawingBufferWidth;
      const h = gl.drawingBufferHeight;
      if (!w || !h) return null;
      const px = new Uint8Array(w * h * 4);
      gl.readPixels(0, 0, w, h, gl.RGBA, gl.UNSIGNED_BYTE, px);
      const b = paintBoxOf(px, w, h);
      if (!b) return null;
      const res = app.renderer.resolution || 1;
      const rr = {
        x: b.minX / res,
        y: (h - 1 - b.maxY) / res,      // readPixels 是左下角原点，翻成屏幕方向
        w: (b.maxX - b.minX + 1) / res,
        h: (b.maxY - b.minY + 1) / res
      };
      lastM.exact = rr;
      return rr;
    }
    const S = size.value;
    const R = MEASURE_SIZE;
    const k = R / S;
    const rt = ensureMeasureRT(R);
    if (!rt) return null;
    const s0 = app.stage.scale.x;
    let px = null;
    try {
      app.stage.scale.set(k);           // 整只缩进小画布里量，量完立刻还原
      app.renderer.render(app.stage, { renderTexture: rt, clear: true });
      px = readBack(rt, R);
    } finally {
      app.stage.scale.set(s0);
    }
    if (!px) return null;
    const b = paintBoxOf(px, R, R);           // px 已经是自上而下的行序
    if (!b) return null;
    const rr = {
      x: b.minX / k,
      y: b.minY / k,
      w: (b.maxX - b.minX + 1) / k,
      h: (b.maxY - b.minY + 1) / k
    };
    lastM.off = rr;
    return rr;
  } catch (e) {
    return null;
  }
};

/** 当前几何快照：{ S: 容器边长, k: 模型缩放, x/y: 模型在画布里的位置 }
 *  摆好一次之后，缩放就是「以容器中心为基准等比放大」——皮套跟着容器一起变大变小，
 *  不会每次重排（重排正是之前「脚被裁/突然变小」的根源）。 */
const geometry = { S: 0, k: 0, x: 0, y: 0 };

const fitModel = () => {
  if (!model || !app) return;
  const S = size.value;
  try {
    app.renderer.resolution = renderResolution(S);
    app.renderer.resize(S, S);
  } catch (e) {}
  let bw = 0, bh = 0, bx = 0, by = 0;
  try {
    const lb = model.getLocalBounds();
    bw = lb.width; bh = lb.height; bx = lb.x; by = lb.y;
  } catch (e) {}
  if (!bw || !bh) {
    bw = model.internalModel.width || 1;
    bh = model.internalModel.height || 1;
    bx = 0; by = 0;
  }
  // 已经摆好过一次：纯等比缩放（以容器中心为基准）
  // —— 皮套跟着容器一起变大变小，不重新排版（重排正是之前「脚被裁 / 突然变小」的根源）
  if (geometry.S > 0 && geometry.k > 0) {
    const ratio = S / geometry.S;
    const c0 = geometry.S / 2;
    const c1 = S / 2;
    const k = geometry.k * ratio;
    model.scale.set(k);
    model.x = c1 + (geometry.x - c0) * ratio;
    model.y = c1 + (geometry.y - c0) * ratio;
    geometry.S = S;
    geometry.k = k;
    geometry.x = model.x;
    geometry.y = model.y;
    return;
  }

  // 第一次：先按 localBounds 摆个初始位置，随后由 refitByPainted() 按真实画面校正
  const scale = Math.min((S * 0.98) / bw, (S * 0.98) / bh);
  model.scale.set(scale);
  model.x = (S - bw * scale) / 2 - bx * scale;
  model.y = (S - bh * scale) / 2 - by * scale;
  geometry.S = S;
  geometry.k = scale;
  geometry.x = model.x;
  geometry.y = model.y;
};

/** 把当前几何写进快照（缩放/移动之后统一调用） */
const snapshotGeometry = (S) => {
  geometry.S = S;
  geometry.k = model.scale.x || geometry.k;
  geometry.x = model.x;
  geometry.y = model.y;
};

/** 按「实际渲染出来的画面」校正到「完整显示 + 四周留白」
 *  为什么要这么做：有些模型的 localBounds 跟真实画面差很多（脚会露在 bounds 外面），
 *  只按 bounds 摆会截脚，所以直接拿渲染出来的像素迭代。
 *  ★ 关键点：画面被画布边缘裁掉时，量到的尺寸是「偏小的」，这时候不能用它算缩放比
 *    （会算出个假的比例来回抖），得先老实缩小几轮，等画面完整落在画布里再精确对位。 */
const convergeFit = (measure, times, tol) => {
  // tol：位置容差（容器像素）。离屏那轮因为小画布只有 160px，
  // 量出来天然带 ±5px 的量化误差，卡 1px 会来回抖、永远「不收敛」——
  // 所以离屏那轮放宽到 ±2 个小画布像素，最后由主画布那一轮收进 1px。
  const T = tol || 1;
  for (let i = 0; i < times; i++) {
    const S = size.value;
    const q = measure();
    if (!q || q.w < 2 || q.h < 2) return false;         // 画面还没出来，交给外层重试
    const eps = 0.5;
    const clipped = q.x <= eps || q.y <= eps || q.x + q.w >= S - eps || q.y + q.h >= S - eps;
    const k = model.scale.x || 1;
    const cx = q.x + q.w / 2;
    const cy = q.y + q.h / 2;
    const zoomAbout = (f) => {                          // 以「画面中心」为基准缩放，中心不动
      model.scale.set(k * f);
      model.x = model.x + (cx - model.x) * (1 - f);
      model.y = model.y + (cy - model.y) * (1 - f);
      snapshotGeometry(S);
    };
    if (window.__refitTrace) window.__refitTrace.push({ i: i, cl: clipped, t: Math.round(q.y), h: Math.round(q.h), k: Math.round(k * 1e5) / 1e5 });
    if (clipped) { zoomAbout(0.94); continue; }         // 被裁了 → 先缩小，直到整只都画得下
    // ★★ 顺序是命门：**先摆正，再放大**
    //  放大是围着「画面中心」长的。画面中心要是偏的，一放大就往画布外顶，
    //  下一轮又判成「被裁」→ 缩小 → 再放大 → 再顶出去……
    //  这一版之前就是这么来回抖了 30 多秒（实测日志 0.0917 → 0.0862 → 0.0810 → 0.0919 死循环）。
    //  先居中之后再放大，放大就是围着画布中心长，长到 92% 也稳稳落在画布里面。
    const dx = (S - q.w) / 2 - q.x;
    const dy = (S - q.h) / 2 - q.y;
    if (Math.abs(dx) > T || Math.abs(dy) > T) {
      model.x += dx;
      model.y += dy;
      snapshotGeometry(S);
      continue;
    }
    const want = S * PAINT_RATIO;                       // 画面只占容器中间的一半
    const f = want / Math.max(q.w, q.h);
    if (f > 1 + Math.max(0.004, T / S)) { zoomAbout(f > 3 ? 3 : f); continue; }
    return true;
  }
  return false;                                          // 还没收敛 → 外层继续排期
};

/** 校正入口：先离屏快速收敛，最后在主画布上精确确认一次
 *  离屏那步主画布完全不动，所以整个校正过程主人在屏幕上什么都看不到
 *  （皮套在校正完之前是透明的，见 ready）—— 也就是「先调整好，再渲染出来」。 */
const refitByPainted = () => {
  if (!model || !app) return false;
  const t0 = (window.performance && performance.now) ? performance.now() : Date.now();
  let ok = false;
  const coarseTol = Math.max(2, Math.ceil(size.value / MEASURE_SIZE) * 2);   // 离屏那轮的容差
  try {
    ok = convergeFit(() => measurePainted(false), 24, coarseTol);   // ① 离屏小画布，快
    if (!ok) ok = convergeFit(() => measurePainted(true), 24, 1);   // 离屏不灵就退回主画布
    if (ok) ok = convergeFit(() => measurePainted(true), 6, 1);     // ② 主画布精确收尾到 1px
  } catch (e) { ok = false; }
  if (ok) {
    const S = size.value;
    const q = lastM.exact || lastM.off;
    if (q) {                                   // 记下画面在容器里的占比：判断「有没有跑丢」要用
      paint.l = Math.max(0, q.x / S);
      paint.t = Math.max(0, q.y / S);
      paint.r = Math.min(1, (q.x + q.w) / S);
      paint.b = Math.min(1, (q.y + q.h) / S);
    }
    saveLayout(0);
    recomputeLost();
    geometry.refitMs = Math.round(((window.performance && performance.now) ? performance.now() : Date.now()) - t0);
  }
  return ok;
};

const react = () => {
  if (!model || dragMoved) { dragMoved = false; return; }
  let played = false;
  try {
    const mm = model.internalModel && model.internalModel.motionManager;
    const defs = (mm && mm.definitions) || {};
    const keys = Object.keys(defs).filter((k) => Array.isArray(defs[k]) && defs[k].length);
    const tap = keys.find((k) => /tap|touch|flick|hit/i.test(k)) || keys[0];
    if (tap) { model.motion(tap); played = true; }
  } catch (e) {}
  const el = wrap.value;
  if (el) {
    el.classList.remove('live2d-bounce');
    void el.offsetWidth;
    el.classList.add('live2d-bounce');
    window.setTimeout(() => el.classList.remove('live2d-bounce'), 480);
  }
  try {
    const core = model.internalModel && model.internalModel.coreModel;
    if (core && core.setParameterValueById) {
      core.setParameterValueById('ParamEyeLOpen', 0);
      core.setParameterValueById('ParamEyeROpen', 0);
      window.setTimeout(() => {
        try {
          core.setParameterValueById('ParamEyeLOpen', 1);
          core.setParameterValueById('ParamEyeROpen', 1);
        } catch (e) {}
      }, 140);
    }
  } catch (e) {}
  if (window.__live2dPuppet) window.__live2dPuppet.lastReact = { at: Date.now(), playedMotion: played };
};

/* 视线跟随
 * 主人要的是「视线的转动中心 = 皮套容器的中心」，方向保持原样（上次我把它上下翻反了，已改回）。
 * 为什么会偏：pixi 的 focus(x, y) 是拿**模型的几何中心**当零点的，而这个模型包围盒上下都超出画布
 * （模型高 2035、画布 1500），几何中心并不在容器正中 —— 于是鼠标摆在容器中心时，视线还是偏的。
 * 这里把鼠标坐标平移一下（模型中心 - 容器中心），让「容器中心」正好对应视线正前方，
 * 方向一点没动。 */
const focusAt = (clientX, clientY) => {
  if (!model || !canvas.value) return;
  const rect = canvas.value.getBoundingClientRect();
  const cx = rect.width / 2;
  const cy = rect.height / 2;                                     // 皮套容器（画面）的中心
  const mcx = model.x + model.width / 2;
  const mcy = model.y + model.height / 2;                         // 模型自己的几何中心
  model.focus((clientX - rect.left) + (mcx - cx), (clientY - rect.top) + (mcy - cy));
};

/* ---------------- 拖动 ---------------- */
const onDragMove = (e) => {
  if (!dragging) return;
  const dx = e.clientX - dragStart.mx;
  const dy = e.clientY - dragStart.my;
  if (!dragMoved && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) dragMoved = true;
  if (!dragMoved) return;
  posX.value = clampX(dragStart.x + dx);
  posY.value = clampY(dragStart.y - dy);
  focusAt(e.clientX, e.clientY);
};

const onDragEnd = () => {
  if (!dragging) return;
  dragging = false;
  document.removeEventListener('mousemove', onDragMove);
  document.removeEventListener('mouseup', onDragEnd);
  if (dragMoved) saveLayout();
};

const onDragStart = (e) => {
  if (e.button !== 0) return;
  dragging = true;
  dragMoved = false;
  dragStart = { mx: e.clientX, my: e.clientY, x: posX.value, y: posY.value };
  document.addEventListener('mousemove', onDragMove);
  document.addEventListener('mouseup', onDragEnd);
};

const onTouchStart = (e) => {
  const t = e.touches && e.touches[0];
  if (!t) return;
  dragging = true;
  dragMoved = false;
  dragStart = { mx: t.clientX, my: t.clientY, x: posX.value, y: posY.value };
};
const onTouchMove = (e) => {
  const t = e.touches && e.touches[0];
  if (!t || !dragging) return;
  const dx = t.clientX - dragStart.mx;
  const dy = t.clientY - dragStart.my;
  if (!dragMoved && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) dragMoved = true;
  if (!dragMoved) return;
  e.preventDefault();
  posX.value = clampX(dragStart.x + dx);
  posY.value = clampY(dragStart.y - dy);
};
const onTouchEnd = () => {
  if (!dragging) return;
  dragging = false;
  if (dragMoved) saveLayout();
};

/* ---------------- 滚轮改大小 ---------------- */
const onWheel = (e) => {
  e.preventDefault();
  // 步长按当前大小走（8%，最少 24px）—— 1000px 的容器上一格 24px 要滚到手酸
  const step = Math.max(24, Math.round(size.value * 0.08));
  const prev = size.value;
  const next = clampSize(prev + (e.deltaY > 0 ? -step : step));
  if (next === prev) return;

  // 以容器【中心】为基准缩放：边长变化量的一半补到 left / bottom 上，
  // 这样放大缩小时是围着中心长大/缩小，而不是从屏幕左下角往外长
  const delta = (prev - next) / 2;
  size.value = next;
  posX.value = clampX(posX.value + delta);
  posY.value = clampY(posY.value + delta);
  fitModel();
  saveLayout(400);
};

/** 换一块干净的 canvas（只有整块拆掉时才需要）
 *  为什么要换：pixi 销毁应用时会把 WebGL 上下文一并丢掉，
 *  同一块 canvas 上再建新应用，拿到的就是那个「死掉」的上下文。 */
const replaceCanvas = () => {
  const old = canvas.value;
  if (!old || !old.parentNode) return;
  const c = document.createElement('canvas');
  c.className = old.className;
  old.parentNode.replaceChild(c, old);
  canvas.value = c;
};

/** 拆掉当前皮套
 *  keepApp = true （换模型：皮套 A → 皮套 B）：只销毁模型，PIXI 应用和 canvas 原样留着
 *  keepApp = false（卸载 / 关掉皮套）：整块拆掉，并换一块干净的 canvas
 *
 *  ★ 这里踩过大坑：以前每次换模型都 app.destroy(true)，会把 canvas 从 DOM 里摘掉、把上下文弄死；
 *    接着新建应用时 shader 编译报 Invalid value of 0 passed to checkMaxIfStatementsInShader，
 *    于是「皮套 A 消失、皮套 B 非得刷新才出来」。 */
const teardown = (keepApp) => {
  if (onMove) { document.removeEventListener('mousemove', onMove); onMove = null; }
  if (onTouch) { document.removeEventListener('touchmove', onTouch); onTouch = null; }
  if (model && app) { try { app.stage.removeChild(model); } catch (e) {} }
  try { if (model) model.destroy(); } catch (e) {}
  model = null;
  loadedPath = '';
  ready.value = false;
  if (keepApp) return;
  if (canvas.value) {
    canvas.value.removeEventListener('webglcontextlost', onCtxLost);
    canvas.value.removeEventListener('webglcontextrestored', onCtxRestored);
  }
  try { if (app) app.destroy(true, { children: true }); } catch (e) {}
  app = null;
  visible.value = false;
  replaceCanvas();
  if (window.__live2dPuppet) delete window.__live2dPuppet;
};

/** 浏览器把 WebGL 上下文收走了（切标签/省电时会发生），画布会变空 —— 重新加载一次 */
const onCtxLost = (e) => {
  if (e && e.preventDefault) e.preventDefault();
  contextLost = true;
  console.warn('[Live2D] WebGL 上下文丢了，稍后自动重载');
  scheduleRetry(1200);
};
const onCtxRestored = () => { contextLost = false; };

const scheduleRetry = (delay) => {
  if (retryTimer || destroyed) return;
  retryTimer = window.setTimeout(async () => {
    retryTimer = 0;
    if (destroyed) return;
    contextLost = false;
    lastSig = '';                        // 强制重来一遍
    const st = lastSetting || await fetchSiteSetting();
    apply(st, true);
  }, delay || 2500);
};

const loadModel = async (path) => {
  if (loading || destroyed) return false;
  loading = true;
  ready.value = false;            // 新的一只：先藏起来，摆好了再露
  try {
    const isCubism2 = /\.model\.json$/i.test(path) && !/\.model3\.json$/i.test(path);
    await loadScript(isCubism2 ? '/live2d-sdk/live2d.min.js' : '/live2d-sdk/live2dcubismcore.min.js');
    const PIXI = await import(/* webpackChunkName: "live2d" */ 'pixi.js');
    window.PIXI = PIXI;
    PIXI_MOD = PIXI;
    const mod = isCubism2
      ? await import(/* webpackChunkName: "live2d" */ 'pixi-live2d-display/cubism2')
      : await import(/* webpackChunkName: "live2d" */ 'pixi-live2d-display/cubism4');
    const Live2DModel = mod.Live2DModel;
    if (destroyed || !canvas.value) return false;

    const S = size.value;
    visible.value = true;         // 先挂上（画布要有尺寸，渲染和量像素都靠它），但还透明
    if (app) {
      // 换模型：复用原来的 PIXI 应用和 canvas（上下文是活的，别折腾它）
      try {
        app.renderer.resolution = renderResolution(S);
        app.renderer.resize(S, S);
      } catch (e) {}
    } else {
      app = new PIXI.Application({
        view: canvas.value,
        width: S,
        height: S,
        backgroundAlpha: 0,
        antialias: true,
        autoStart: true,
        // 显示尺寸可以无限放大，但后备缓冲封顶 2048（超了就降分辨率），免得把显卡拖死
        resolution: renderResolution(S),
        autoDensity: true
      });
      canvas.value.addEventListener('webglcontextlost', onCtxLost, false);
      canvas.value.addEventListener('webglcontextrestored', onCtxRestored, false);
    }

    const m = await Live2DModel.from(path, { autoInteract: false });
    if (destroyed) { try { m.destroy(); } catch (e) {} return false; }
    model = m;
    loadedPath = path;
    contextLost = false;

    app.stage.addChild(model);
    geometry.S = 0;
    geometry.k = 0;
    fitModel();                        // 先按 bounds 摆一次（贴图还没就绪时只能这样）
    // 这只模型以前摆好过 → 直接套用存下来的几何，不用再量，皮套一出来就是正好的
    const cachedFit = readFitCache(path);
    if (cachedFit) {
      applyFit(cachedFit);
      ready.value = true;              // 有缓存 = 已经是摆好的，直接露出来（模型一画出来就是对的）
    }
    // 贴图是异步解码的，刚 addChild 那会儿画布上多半还是空的；
    // 隔一会儿开始按「实际渲染出来的画面」反复校正（比按 localBounds 准得多）
    // 贴图是异步解码的（大模型在软件渲染下要十几二十秒），所以这里一直排期重试，
    // 直到画面真的完整落在容器里为止；成功后隔几秒再确认一次，防止贴图迟到把尺寸改了。
    let tryTimes = 0;
    const refitLog = [];
    const tryRefit = () => {
      if (destroyed || !model || !app) return;
      tryTimes += 1;
      const t0 = (window.performance && performance.now) ? performance.now() : Date.now();
      if (refitByPainted()) {
        ready.value = true;                                    // 摆好了，这时候才露出来
        writeFitCache(path);                                   // 存下来，下次进这个模型就不用再量
        if (window.__live2dPuppet) {
          window.__live2dPuppet.geometry = { S: geometry.S, k: geometry.k, x: geometry.x, y: geometry.y };
          window.__live2dPuppet.refitTimes = tryTimes;
          window.__live2dPuppet.refitMs = geometry.refitMs;
        }
        if (tryTimes < 3) window.setTimeout(tryRefit, 2500);   // 再确认一两次
        return;
      }
      if (refitLog.length < 60) {
        const box = (b) => (b ? { t: Math.round(b.y), b: Math.round(b.y + b.h), w: Math.round(b.w), h: Math.round(b.h) } : null);
        refitLog.push({
          n: tryTimes,
          ms: Math.round(((window.performance && performance.now) ? performance.now() : Date.now()) - t0),
          离屏: box(lastM.off), 主画布: box(lastM.exact)
        });
        if (window.__live2dPuppet) window.__live2dPuppet.refitLog = refitLog;
      }
      if (tryTimes < 90) window.setTimeout(tryRefit, 500);
    };
    window.setTimeout(tryRefit, 500);
    // 兜底：万一这只模型一直量不出画面（贴图坏了之类），20 秒后也得露出来，不能一直隐身
    window.setTimeout(() => { if (!destroyed && model === m && !ready.value) ready.value = true; }, 4000);

    onMove = (e) => focusAt(e.clientX, e.clientY);
    onTouch = (e) => { const t = e.touches && e.touches[0]; if (t) focusAt(t.clientX, t.clientY); };
    document.addEventListener('mousemove', onMove, { passive: true });
    document.addEventListener('touchmove', onTouch, { passive: true });

    window.__live2dPuppet = { app: app, model: model, path: path, geometry: null, fitFromCache: !!cachedFit, layout: { size: size.value, x: posX.value, y: posY.value } };
    return true;
  } catch (e) {
    console.warn('[Live2D] 皮套加载失败：' + path + ' → ' + ((e && e.message) ? e.message : e));
    try { if (model) model.destroy(); } catch (err) {}
    try { if (app) app.destroy(true, { children: true }); } catch (err) {}
    model = null;
    app = null;
    return false;
  } finally {
    loading = false;
  }
};

/** 把设置算成一个「指纹」，只有它变了才需要重新加载 */
const makeSig = (st) => {
  const enabled = Number(st && st.live2d_enabled) === 1 ? '1' : '0';
  let picks = parsePicks(st && st.live2d_picks);
  if (!picks.length && st && st.live2d_path) picks = [String(st.live2d_path)];
  return enabled + '|' + picks.join('|');
};

const runApply = async (st, force) => {
  const sig = makeSig(st);
  const enabled = sig.charAt(0) === '1';
  let picks = parsePicks(st && st.live2d_picks);
  if (!picks.length && st && st.live2d_path) picks = [String(st.live2d_path)];

  if (!enabled || !picks.length) {
    lastSig = sig;
    teardown(false);            // 关掉了：整块拆掉，连 canvas 一起换新的
    return;
  }
  // 设置没变、模型也还在 → 什么都不做
  // （切标签回来、切路由、别的标签页喊一声，走到这儿就自然忽略了 —— 之前皮套会消失就是被 force 重来打断的）
  if (!force && sig === lastSig && model && !contextLost) return;

  // 上下文丢了才会整块重来（那时候必须连 canvas 一起换新的）
  teardown(!contextLost);
  lastSig = sig;

  const order = picks.slice();
  for (let i = order.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const t = order[i]; order[i] = order[j]; order[j] = t;
  }
  for (const p of order) {
    if (destroyed) return;
    if (await loadModel(p)) return;
  }
  console.warn('[Live2D] 随机池里的模型都加载不出来：', order.join(' / '));
  lastSig = '';                  // 允许下次重试
  scheduleRetry(4000);
};

/** 串行执行，避免两次「应用设置」叠在一起互相打断 */
const apply = (st, force) => {
  lastSetting = st || lastSetting;
  chain = chain.then(() => runApply(st, force)).catch(() => {});
  return chain;
};

const onWindowResize = () => {
  size.value = clampSize(size.value);   // 窗口变小 → 皮套跟着缩，保证还是完整的
  fitModel();
  recomputeLost();
  saveLayout(500);
};

onMounted(async () => {
  // 兜底：皮套真找不着了，用 ?reset_live2d=1 进一次就复位（位置 + 摆好的几何一起清）
  try {
    if (/[?&](reset_live2d|puppet=reset)/i.test(String(window.location.search || ''))) {
      localStorage.removeItem(LS_KEY);
      localStorage.removeItem(FIT_KEY);
    }
  } catch (e) {}
  loadLayout();          // 本地有就用本地的，没有再给默认
  // 存下来的位置过一遍范围检查（只防止跑得没边），界外位置照旧保留、不拉回屏幕
  posX.value = clampX(posX.value);
  posY.value = clampY(posY.value);
  saveLayout(0);
  document.addEventListener('touchmove', onTouchMove, { passive: false });
  document.addEventListener('touchend', onTouchEnd);
  window.addEventListener('resize', onWindowResize);

  const st = await fetchSiteSetting();
  await apply(st, false);
  offSettings = onSettingsChanged(async () => {
    if (destroyed) return;
    const latest = await fetchSiteSetting();
    // 注意这里是 force=false：设置真变了（指纹不同）才重来，切标签回来不会重来
    await apply(latest, false);
  });

});

onBeforeUnmount(() => {
  destroyed = true;
  document.removeEventListener('touchmove', onTouchMove);
  document.removeEventListener('touchend', onTouchEnd);
  window.removeEventListener('resize', onWindowResize);
  document.removeEventListener('mousemove', onDragMove);
  document.removeEventListener('mouseup', onDragEnd);
  if (saveTimer) clearTimeout(saveTimer);
  if (retryTimer) clearTimeout(retryTimer);
  if (offSettings) { offSettings(); offSettings = null; }
  teardown(false);              // 组件卸载：整块拆
});
</script>

<style scoped>
.live2d-puppet {
  position: fixed;
  left: 0;
  bottom: 0;
  /* 主人要求：皮套夹在「卡片」和「背景图」中间 ——
     背景层是 z-index:-1，页面卡片(link-integral-container / navbar)是 1，
     所以这里给 0：能看见、但被卡片挡住，不会压在内容上面。
     （下面那个「皮套回来」按钮仍然保持在最上层，不然点不着。） */
  z-index: 0;
  pointer-events: auto;
  cursor: grab;
  touch-action: none;
}

.live2d-puppet { transition: opacity .28s ease; }

/* 皮套跑到屏幕外时的「叫回来」按钮 */
.live2d-find {
  position: fixed;
  left: 12px;
  bottom: 12px;
  z-index: 40;
  padding: 6px 12px;
  border: 0;
  border-radius: 999px;
  background: rgba(64, 158, 255, .92);
  color: #fff;
  font-size: 12px;
  line-height: 1.4;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(0, 0, 0, .2);
}

.live2d-find:hover { background: rgba(64, 158, 255, 1); }

.live2d-puppet:active { cursor: grabbing; }

.live2d-puppet.live2d-bounce { animation: live2d-bounce .46s ease-out; }

@keyframes live2d-bounce {
  0%   { transform: translateY(0) scale(1); }
  30%  { transform: translateY(-10px) scale(1.04); }
  60%  { transform: translateY(0) scale(.985); }
  100% { transform: translateY(0) scale(1); }
}

.live2d-canvas {
  width: 100%;
  height: 100%;
  display: block;
  pointer-events: none;
}

/* 手机上把整块等比缩小，不然 1000px 的容器太占地方（桌面端不受影响） */
@media (max-width: 768px) {
  .live2d-puppet {
    transform: scale(0.42);
    transform-origin: left bottom;
  }
}
</style>

